import { hasStylesToRemove, IEditorV3 } from "@azu-business/editor-v3";
import { AioExternalReplacements, AioExternalSingle } from "aic";
import { getHierarchyBaseUrl } from "contexts/hierarchy/getHierarchyBaseUrl";
import { eSingleSource } from "contexts/hierarchy/interface";
import { STYLES_TO_CLEAN, STYLES_TO_DELETE } from "../../../config";
import { hasStylesInReplacementValues } from "./hasStylesInReplacementValues";
import { HierarchyLinksParams, HierarchyReplacementLinkDescriptor } from "./interface";

/**
 * Push a hierarchy replacement link descriptor into `links` when `condition` is true.
 *
 * @param links - Accumulator array for link descriptors
 * @param condition - Whether to push the link
 * @param baseUrl - Base URL for the hierarchy
 * @param hierarchyName - Display name of the hierarchy
 * @param type - 'singles' or 'lists'
 * @param keyPrefix - Prefix used to form React keys
 */
const pushLinkIf = (
  links: HierarchyReplacementLinkDescriptor[],
  condition: boolean,
  baseUrl: string,
  hierarchyName: string,
  type: "singles" | "lists",
  keyPrefix: string,
) => {
  if (condition) {
    links.push({ baseUrl, hierarchyName, type, keyPrefix });
  }
};

/**
 * Generates descriptors for links to hierarchy levels containing styled replacements (singles and/or lists).
 * Checks both the current hierarchy and parent templates to find sources of styled content.
 * Used in the RemoveStylesModal to warn users about external styled replacements that will be embedded.
 *
 * @param params - The parameters containing hierarchy data and external styled replacements
 * @param params.hierarchy - The current hierarchy node
 * @param params.parentTemplates - Parent template data containing singles and lists
 * @param params.hierarchyNodes - All available hierarchy nodes for finding parent hierarchy
 * @param params.externalSinglesWithStyles - External singles with styles that need to be displayed in warning
 * @param params.externalListsWithStyles - External lists with styles that need to be displayed in warning
 * @returns Array of link descriptor objects for relevant hierarchy replacements pages
 */
export const getHierarchyLinksDescriptorsForStyledReplacements = ({
  hierarchy,
  parentTemplates,
  hierarchyNodes,
  externalSinglesWithStyles,
  externalListsWithStyles,
}: HierarchyLinksParams): HierarchyReplacementLinkDescriptor[] => {
  const links: HierarchyReplacementLinkDescriptor[] = [];

  // Current hierarchy
  const currentHasStyledSingles = hierarchy.singles.some((single: AioExternalSingle<IEditorV3>) =>
    hasStylesToRemove(single.newText, [...STYLES_TO_DELETE, ...STYLES_TO_CLEAN]),
  );
  const currentHasStyledLists = hierarchy.lists.some((list: AioExternalReplacements<IEditorV3>) =>
    hasStylesInReplacementValues(list.newTexts),
  );
  const hasExternalSinglesFromCurrent = externalSinglesWithStyles.some(
    (single) => single.source === eSingleSource.hierarchy,
  );
  const hasExternalListsFromCurrent = externalListsWithStyles.length > 0;
  const currentBaseUrl = getHierarchyBaseUrl(hierarchy);
  pushLinkIf(
    links,
    currentHasStyledSingles && hasExternalSinglesFromCurrent,
    currentBaseUrl,
    hierarchy.name,
    "singles",
    "current",
  );
  pushLinkIf(
    links,
    currentHasStyledLists && hasExternalListsFromCurrent,
    currentBaseUrl,
    hierarchy.name,
    "lists",
    "current",
  );

  // Parent hierarchy - group by fromHierarchyId to find which parent hierarchies have styled content
  if (hierarchyNodes) {
    // Collect unique hierarchy IDs from parent templates with styles
    const parentHierarchyIds = new Set<string>();
    parentTemplates.singles.forEach((single) => {
      /* istanbul ignore else */
      if (hasStylesToRemove(single.newText, [...STYLES_TO_DELETE, ...STYLES_TO_CLEAN])) {
        parentHierarchyIds.add(single.fromHierarchyId);
      }
    });
    parentTemplates.lists.forEach((list) => {
      /* istanbul ignore else */
      if (hasStylesInReplacementValues(list.newTexts)) {
        parentHierarchyIds.add(list.fromHierarchyId);
      }
    });

    // Generate links for each parent hierarchy with styled content
    parentHierarchyIds.forEach((hierarchyId) => {
      const parentNode = hierarchyNodes.find((node) => node.id === hierarchyId);
      if (parentNode) {
        const parentBaseUrl = getHierarchyBaseUrl(parentNode);
        pushLinkIf(
          links,
          parentTemplates.singles.some(
            (s) =>
              s.fromHierarchyId === hierarchyId &&
              hasStylesToRemove(s.newText, [...STYLES_TO_DELETE, ...STYLES_TO_CLEAN]),
          ),
          parentBaseUrl,
          parentNode.name,
          "singles",
          "parent",
        );
        pushLinkIf(
          links,
          parentTemplates.lists.some(
            (l) => l.fromHierarchyId === hierarchyId && hasStylesInReplacementValues(l.newTexts),
          ),
          parentBaseUrl,
          parentNode.name,
          "lists",
          "parent",
        );
      }
    });
  }
  return links;
};
